import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"
import { Modal } from "antd"
import { useStoreState, StateMapper } from "easy-peasy"
import { AppDataStore } from "../appData/types"
import { gen, swimminglyApi } from "./utils"
import type { FeatureKeyType, LockSource } from "../constants/featureAccess"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Default locked-modal body copy keyed by LockSource.
 * Mirrors DEFAULT_LOCKED_MODAL_COPY in helpers/featureAccessRegistry.js.
 */
const DEFAULT_LOCKED_MODAL_COPY: Record<NonNullable<LockSource>, string> = {
  systemGuest:
    "This feature is not available for Guest \u2013 Lite Access clubs. Contact Swimmingly to upgrade your club\u2019s membership and unlock full access.",
  systemCustomer:
    "Your current plan does not include this feature. Contact Swimmingly to discuss upgrading your access.",
  org: "This feature has been disabled for your organization. Contact Swimmingly for more information.",
  club: "This feature has been disabled for your club. Contact Swimmingly for more information.",
  clubSeason:
    "This feature has been disabled for the current season. Contact Swimmingly for more information.",
  league: "This feature has been disabled for your league. Contact Swimmingly for more information.",
}

export interface FeatureAccessContextValue {
  /**
   * True if any active season for the current club has isSwimminglyCustomer = 0.
   * Kept for informational UI (e.g. "Guest" tags, context-aware messaging).
   */
  isGuestClub: boolean
  /**
   * Returns true when the given feature key is locked for the current club.
   */
  isFeatureLocked: (key: FeatureKeyType | string) => boolean
  /**
   * Returns the scope tier that produced the lock, or null if not locked.
   * Use this to customise messaging ("locked because you're a guest" vs
   * "locked because of your plan tier").
   */
  getLockSource: (key: FeatureKeyType | string) => LockSource
  /**
   * Opens the locked-feature modal with copy appropriate for the lock source
   * of the given feature key. Provide the feature key so the modal can show
   * feature-specific or lock-reason-specific messaging.
   */
  openLockedModal: (key: FeatureKeyType | string) => void
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const FeatureAccessContext = createContext<FeatureAccessContextValue>({
  isGuestClub: false,
  isFeatureLocked: () => false,
  getLockSource: () => null,
  openLockedModal: () => undefined,
})

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function FeatureAccessProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const impersonateClub = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedClub,
  )
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const aliasedUser = useStoreState(
    (state: StateMapper<AppDataStore>) => state.aliasedUser,
  )

  const [features, setFeatures] = useState<Record<string, boolean>>({})
  const [lockSources, setLockSources] = useState<
    Record<string, LockSource>
  >({})
  const [customMessages, setCustomMessages] = useState<Record<string, string | null>>({})
  const [isGuestClub, setIsGuestClub] = useState(false)

  // Modal state: key → modal body text when open
  const [modalState, setModalState] = useState<{
    visible: boolean
    title: string
    body: string
  }>({ visible: false, title: "Feature not available", body: "" })

  useEffect(() => {
    const clubId = impersonateClub?.clubId

    if (!clubId) {
      setFeatures({})
      setLockSources({})
      setCustomMessages({})
      setIsGuestClub(false)
      return
    }

    // Swimmingly admins (role 2) bypass feature locks but still need isGuestClub
    // for informational UI (e.g. hiding the Billing sidebar tab for guest clubs).
    const effectiveRole = aliasedUser ? aliasedUser.role : user?.role
    const isAdminViewing = effectiveRole === 2

    swimminglyApi
      .get(gen(`/api/featureAccess/${clubId}`))
      .then((response: any) => {
        if (response?.status !== "success") return
        setIsGuestClub(response.isGuestClub ?? false)
        if (isAdminViewing) {
          // Admins are not blocked by feature locks; clear them but keep isGuestClub.
          setFeatures({})
          setLockSources({})
          setCustomMessages({})
        } else {
          setFeatures(response.features ?? {})
          setLockSources(response.lockSources ?? {})
          setCustomMessages(response.customMessages ?? {})
        }
      })
      .catch(() => {
        setFeatures({})
        setLockSources({})
        setCustomMessages({})
        setIsGuestClub(false)
      })
  }, [impersonateClub?.clubId, user?.role, aliasedUser])

  const isFeatureLocked = useCallback(
    (key: string) => features[key] === true,
    [features],
  )

  const getLockSource = useCallback(
    (key: string): LockSource => lockSources[key] ?? null,
    [lockSources],
  )

  const openLockedModal = useCallback(
    (key: string) => {
      const source: LockSource = lockSources[key] ?? null

      // Title adapts to lock source
      let title = "Feature not available"
      if (source === "systemGuest") {
        title = "Feature not available \u2013 Lite Access only"
      } else if (source === "systemCustomer") {
        title = "Feature not available on your current plan"
      }

      // Body: prefer admin-set custom message, then DEFAULT copy keyed by lock source
      const adminMessage = customMessages[key]
      const body = adminMessage
        ? adminMessage
        : source
          ? DEFAULT_LOCKED_MODAL_COPY[source]
          : "This feature is not available for your club. Contact Swimmingly for more information."

      setModalState({ visible: true, title, body })
    },
    [lockSources, customMessages],
  )

  const value = useMemo<FeatureAccessContextValue>(
    () => ({ isGuestClub, isFeatureLocked, getLockSource, openLockedModal }),
    [isGuestClub, isFeatureLocked, getLockSource, openLockedModal],
  )

  return (
    <FeatureAccessContext.Provider value={value}>
      {children}
      <Modal
        title={modalState.title}
        open={modalState.visible}
        onOk={() => setModalState((s) => ({ ...s, visible: false }))}
        onCancel={() => setModalState((s) => ({ ...s, visible: false }))}
        okText="Got it"
        cancelButtonProps={{ style: { display: "none" } }}
        centered
      >
        <p>{modalState.body}</p>
      </Modal>
    </FeatureAccessContext.Provider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useFeatureAccess(): FeatureAccessContextValue {
  return useContext(FeatureAccessContext)
}

// ---------------------------------------------------------------------------
// FeatureLockOverlay
// Wraps children in a semi-transparent, click-intercepting overlay when
// the given feature is locked for the current club.
// ---------------------------------------------------------------------------

export function FeatureLockOverlay({
  featureKey,
  children,
}: {
  featureKey: FeatureKeyType | string
  children: React.ReactNode
}) {
  const { isFeatureLocked, openLockedModal } = useFeatureAccess()
  const locked = isFeatureLocked(featureKey)

  if (!locked) return <>{children}</>

  return (
    <div style={{ position: "relative" }}>
      <div
        style={{ opacity: 0.6, pointerEvents: "none", userSelect: "none" }}
      >
        {children}
      </div>
      <div
        style={{
          position: "absolute",
          inset: 0,
          cursor: "not-allowed",
          zIndex: 10,
        }}
        onClick={() => openLockedModal(featureKey)}
      />
    </div>
  )
}
